/**
 * OKS-63 — Inertia prop shapes for the brand-side Mission Analytics
 * surface.
 *
 * Two routes, two prop bags:
 *
 *   - `/brand/analytics/missions` →
 *     `MissionAnalyticsController#index`  → `MissionAnalyticsOverviewProps`
 *   - `/brand/missions/:id/analytics` →
 *     `MissionAnalyticsController#show`   → `MissionAnalyticsDetailProps`
 *
 * Mirrors the server-side `BrandOps::MissionAnalytics{Overview,Detail}`
 * `Result` struct shapes 1:1. All keys are snake_case (the Inertia
 * JSON round-trip preserves the Ruby serializer's keys).
 *
 * # 4-state matrix (overview surface)
 *
 * `state` discriminates the FE rendering branch:
 *
 *   - `"cold_start"`     — brand has zero missions lifetime; render the
 *                          `<AnalyticsEmptyState variant="cold_start">`
 *                          page. `kpis`/`trend`/`top_missions` are null.
 *   - `"empty_in_window"`— brand has missions but no collabs in the
 *                          filtered window. `kpis` are all zeros, `trend`
 *                          is `[]`, `top_missions` is `[]`. FE renders
 *                          the populated layout with per-section empty
 *                          cards.
 *   - `"success"`        — all sections populated.
 *   - `"error"`          — `rescue StandardError` branch. All data props
 *                          null; FE renders `<AnalyticsErrorState>` with
 *                          a Retry button.
 *
 * No `"loading"` state — Inertia partial reloads drive the skeleton
 * swap, FE manages the in-flight flag locally.
 */

// =========================================================================
// Shared whitelists — mirror `BrandOps::MissionAnalyticsFilters` constants
// =========================================================================

export const MISSION_TYPE_VALUES = [
  "all",
  "drop",
  "promotional",
  "open",
] as const
export type MissionTypeFilter = (typeof MISSION_TYPE_VALUES)[number]

export const WINDOW_VALUES = ["30d", "90d", "all"] as const
export type AnalyticsWindow = (typeof WINDOW_VALUES)[number]

export interface AnalyticsFilters {
  mission_type: MissionTypeFilter
  window: AnalyticsWindow
}

// =========================================================================
// Overview (cross-mission) — `/brand/analytics/missions`
// =========================================================================

export type AnalyticsOverviewState =
  | "cold_start"
  | "empty_in_window"
  | "success"
  | "error"

/**
 * Cross-mission KPI bag. Numerator + denominator fields are explicit
 * Integers (TRD K8.1) — FE renders the tooltip directly without
 * re-deriving via `Math.round(rate × total)` (which rounds floats
 * unreliably). Rate is `null` when denominator is zero (FE renders
 * `"—"`).
 */
export interface MissionAnalyticsOverviewKpis {
  total_missions: number
  total_collabs: number
  acceptance_rate: number | null
  acceptance_numerator: number
  acceptance_denominator: number
  completion_rate: number | null
  completion_numerator: number
  completion_denominator: number
}

/**
 * Single bucket in the daily trend chart. Date is `"YYYY-MM-DD"` UTC
 * (TRD K6); FE parses via `Intl.DateTimeFormat` only for display.
 */
export interface MissionAnalyticsTrendPoint {
  date: string
  completions: number
  approvals: number
}

/**
 * Single row in the top-5 best-performers table. Ranking is lifetime
 * (NOT window-cut — PRD AC §5; TRD K9); mission_type filter does apply.
 */
export interface MissionAnalyticsTopMissionRow {
  id: number
  title: string
  mission_type: "drop" | "promotional" | "open"
  total_collabs: number
  completion_rate: number | null
  approved_submissions: number
}

/** Full Inertia prop bag for `Brand/Analytics/Missions/Index`. */
export interface MissionAnalyticsOverviewProps {
  state: AnalyticsOverviewState
  filters: AnalyticsFilters
  kpis: MissionAnalyticsOverviewKpis | null
  trend: MissionAnalyticsTrendPoint[] | null
  top_missions: MissionAnalyticsTopMissionRow[] | null
}

// =========================================================================
// Detail (per-mission) — `/brand/missions/:id/analytics`
// =========================================================================

/**
 * Thin header slice — title + status + type. The deep-dive page only
 * needs these for the heading + draft banner + "View mission" link.
 * Full Mission serialization is NOT emitted on this surface.
 */
export interface MissionAnalyticsHeader {
  id: number
  title: string
  mission_type: "drop" | "promotional" | "open"
  status: "draft" | "active"
}

/**
 * Per-mission KPI bag — same `*_numerator/denominator` Integer fields
 * as the overview, plus `approved_submissions`. `total_missions` is
 * NOT carried (this is a single-mission surface).
 */
export interface MissionAnalyticsDetailKpis {
  total_collabs: number
  acceptance_rate: number | null
  acceptance_numerator: number
  acceptance_denominator: number
  completion_rate: number | null
  completion_numerator: number
  completion_denominator: number
  approved_submissions: number
}

/**
 * One per `Collab::STATES` (14 states, including zero-count buckets).
 * Server emits RAW per-state counts in `FUNNEL_STATE_ORDER` (TRD K10);
 * the FE combines display buckets (e.g. `shipping = to_ship + shipped
 * + delivered`) per UX Spec §2.
 *
 * ⚠️ The union below must track `Collab::STATES`. Nothing enforces it: the
 * Inertia prop boundary is untyped, so `npm run check` stays green on a
 * union that no longer describes the payload. OKS-1005 widened it with
 * `FUNNEL_STATE_ORDER`; `expired` / `reopen_requested` reach this prop but
 * are deliberately not drawn (see `buildDisplayRows`' bucket list).
 */
export interface MissionAnalyticsFunnelBucket {
  state:
    | "assigned"
    | "applied"
    | "accepted"
    | "rejected"
    | "purchased"
    | "to_ship"
    | "shipped"
    | "delivered"
    | "posted"
    | "completed"
    | "declined"
    | "issue"
    | "expired"
    | "reopen_requested"
  count: number
}

/** Single row in the paginated per-creator table. */
export interface MissionAnalyticsCreatorRow {
  collab_id: number
  /** May be `null` when the creator hasn't connected TikTok yet. */
  tiktok_handle: string | null
  state: MissionAnalyticsFunnelBucket["state"]
  days_since_assigned: number
  /** Count of ALL submissions on the collab (pending + approved + flagged), capped at 3. */
  submission_count: number
}

/**
 * 5-key pagy projection — mirrors the `MissionsController#
 * pagination_props` contract.
 */
export interface MissionAnalyticsPagination {
  page: number
  pages: number
  count: number
  prev: number | null
  next: number | null
}

export interface MissionAnalyticsCreatorsPage {
  rows: MissionAnalyticsCreatorRow[]
  pagination: MissionAnalyticsPagination
}

/** Full Inertia prop bag for `Brand/Analytics/Missions/Show`. */
export interface MissionAnalyticsDetailProps {
  mission: MissionAnalyticsHeader
  kpis: MissionAnalyticsDetailKpis | null
  funnel: MissionAnalyticsFunnelBucket[] | null
  creators_page: MissionAnalyticsCreatorsPage | null
  /** Set only on the controller's `rescue StandardError` branch. */
  error?: boolean
}
